import { CheckCircle2, Circle, ChevronRight } from "lucide-react";
import Link from "next/link";

interface ChecklistItem {
  label: string;
  complete: boolean;
  href: string;
  description: string;
}

interface ProfileChecklistProps {
  items: ChecklistItem[];
  userType: "contractor" | "worker";
}

export function ProfileChecklist({ items, userType }: ProfileChecklistProps) {
  const completed = items.filter((i) => i.complete).length;
  const total = items.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (completed === total) return null; // Hide when 100%

  return (
    <div className="rounded-2xl border-2 border-brand-orange/30 bg-brand-orange/5 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-base">Complete Your Profile</h3>
        <span className="text-sm font-semibold text-brand-orange">
          {percent}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-muted mb-4 overflow-hidden">
        <div
          className="h-full rounded-full bg-brand-orange transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="space-y-2">
        {items
          .filter((item) => !item.complete)
          .slice(0, 3)
          .map((item, i) => (
            <Link
              key={i}
              href={item.href}
              className="flex items-center gap-3 rounded-xl border bg-background p-3 hover:bg-muted/50 transition-colors active:scale-[0.98]"
            >
              <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {item.description}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          ))}
      </div>

      {items.filter((i) => i.complete).length > 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          ✓ {completed} of {total} completed
        </p>
      )}
    </div>
  );
}

/**
 * Build checklist items from profile data
 */
export function getContractorChecklist(data: {
  hasName: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  hasCompanyName: boolean;
  hasAbn: boolean;
  hasAddress: boolean;
  hasIndustry: boolean;
  hasWebsite: boolean;
  hasLogo: boolean;
  hasPostedJob: boolean;
  hasContacts: boolean;
}): ChecklistItem[] {
  return [
    {
      label: "Add your name",
      complete: data.hasName,
      href: "/contractor/profile/edit",
      description: "Workers see who's hiring them",
    },
    {
      label: "Add phone number",
      complete: data.hasPhone,
      href: "/contractor/profile/edit",
      description: "So hired workers can call you",
    },
    {
      label: "Company name",
      complete: data.hasCompanyName,
      href: "/contractor/profile/edit",
      description: "Your business identity on RateRight",
    },
    {
      label: "Add your ABN",
      complete: data.hasAbn,
      href: "/contractor/profile/edit",
      description: "Builds trust with workers",
    },
    {
      label: "Business address",
      complete: data.hasAddress,
      href: "/contractor/profile/edit",
      description: "Helps match nearby workers",
    },
    {
      label: "Upload company logo",
      complete: data.hasLogo,
      href: "/contractor/profile",
      description: "Stand out from other contractors",
    },
    {
      label: "Add team contacts",
      complete: data.hasContacts,
      href: "/contractor/contacts",
      description: "Site supers workers can call directly",
    },
    {
      label: "Post your first job",
      complete: data.hasPostedJob,
      href: "/contractor/post-job",
      description: "Start finding workers",
    },
  ];
}

export function getWorkerChecklist(data: {
  hasName: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  hasTrades: boolean;
  hasExperience: boolean;
  hasSuburb: boolean;
  hasWhiteCard: boolean;
  hasAvatar: boolean;
}): ChecklistItem[] {
  return [
    {
      label: "Add your name",
      complete: data.hasName,
      href: "/worker/profile/edit",
      description: "Contractors need to know who you are",
    },
    {
      label: "Add phone number",
      complete: data.hasPhone,
      href: "/worker/profile/edit",
      description: "So contractors can call you",
    },
    {
      label: "Add your trades",
      complete: data.hasTrades,
      href: "/worker/profile/edit",
      description: "Get matched to the right jobs",
    },
    {
      label: "Years of experience",
      complete: data.hasExperience,
      href: "/worker/profile/edit",
      description: "Experienced workers get hired faster",
    },
    {
      label: "Your location",
      complete: data.hasSuburb,
      href: "/worker/profile/edit",
      description: "Find jobs near you",
    },
    {
      label: "White Card details",
      complete: data.hasWhiteCard,
      href: "/worker/profile/edit",
      description: "Required for all construction sites",
    },
    {
      label: "Profile photo",
      complete: data.hasAvatar,
      href: "/worker/profile",
      description: "Workers with photos get 3x more views",
    },
  ];
}
